import {
  brandAgentEmailDomain,
  brandEmailAddress,
  brandSupportEmail,
} from "@app/lib/brand_domain";
import { DEFAULT_PRESTOP_DRAIN_DURATION_MS } from "@app/lib/constants/timeouts";
import { isDevelopment } from "@app/types/shared/env";
import { EnvironmentConfig } from "@app/types/shared/utils/config";

const PRODUCTION_DUST_API = "https://dust.tt";

// Pluggable base URL resolver (e.g. RegionContext in the SPA).
let baseUrlResolver: (() => string) | null = null;

export function setBaseUrlResolver(fn: (() => string) | null): void {
  baseUrlResolver = fn;
}

// Returns the resolver's URL if set, or empty string.
// Used by clientFetch to decide whether to rewrite relative URLs (SPA cross-origin only).
export function getBaseUrl(): string {
  return baseUrlResolver?.() || "";
}

// Pluggable default RequestInit resolver (e.g. credentials/headers per context).
// The resolver may be async (e.g. to call getAccessToken() which refreshes expired tokens).
let defaultInitResolver: (() => Promise<RequestInit>) | null = null;

export function setDefaultInitResolver(
  fn: (() => Promise<RequestInit>) | null
): void {
  defaultInitResolver = fn;
}

export function getDefaultInit(): Promise<RequestInit> | null {
  return defaultInitResolver?.() ?? null;
}

const config = {
  getPreStopDrainDurationMs: (): number => {
    const value = EnvironmentConfig.getOptionalEnvVariable(
      "PRESTOP_DRAIN_DURATION_SECONDS"
    );
    if (!value) {
      return DEFAULT_PRESTOP_DRAIN_DURATION_MS;
    }

    const durationSeconds = Number(value);
    if (!Number.isSafeInteger(durationSeconds) || durationSeconds <= 0) {
      throw new Error(
        "PRESTOP_DRAIN_DURATION_SECONDS must be a positive integer"
      );
    }

    return durationSeconds * 1_000;
  },

  // Dynamic API base URL: uses a custom resolver when set (SPA region switching),
  // otherwise falls back to getClientFacingUrl().
  getApiBaseUrl: (): string => {
    const url = baseUrlResolver?.();
    if (url) {
      return url;
    }

    // We override the NEXT_PUBLIC_DUST_API_URL in `front-internal` to ensure that the
    // uploadUrl returned by the file API points to the `http://front-internal-service` and not our
    // public API URL.
    let override = EnvironmentConfig.getOptionalEnvVariable(
      "DUST_INTERNAL_API_URL"
    );
    if (override) {
      return override;
    }

    // Using process.env here to make sure the function is usable on the client side.
    if (!process.env.NEXT_PUBLIC_DUST_API_URL) {
      throw new Error("NEXT_PUBLIC_DUST_API_URL is not set");
    }
    return process.env.NEXT_PUBLIC_DUST_API_URL;
  },

  // Public URL Google (and other inbound webhooks) can POST to. Unlike
  // getApiBaseUrl(), this skips DUST_INTERNAL_API_URL so a local tunnel
  // (ngrok) can be advertised without rerouting in-process API calls.
  getPublicApiUrl: (): string => {
    if (!process.env.NEXT_PUBLIC_DUST_API_URL) {
      throw new Error("NEXT_PUBLIC_DUST_API_URL is not set");
    }
    return process.env.NEXT_PUBLIC_DUST_API_URL.replace(/\/$/, "");
  },

  getStaticWebsiteUrl: (): string => {
    // Using process.env here to make sure the function is usable on the client side.
    if (!process.env.NEXT_PUBLIC_DUST_STATIC_WEBSITE_URL) {
      throw new Error("NEXT_PUBLIC_DUST_STATIC_WEBSITE_URL is not set");
    }
    return process.env.NEXT_PUBLIC_DUST_STATIC_WEBSITE_URL;
  },
  // URL for the main app pages (/w/..., /share/..., etc.).
  // Use this for page URLs, not API endpoints.
  getAppUrl: (): string => {
    // Using process.env here to make sure the function is usable on the client side.
    if (!process.env.NEXT_PUBLIC_DUST_APP_URL) {
      throw new Error("NEXT_PUBLIC_DUST_APP_URL is required");
    }

    return process.env.NEXT_PUBLIC_DUST_APP_URL;
  },
  // URL for the poke app (front-spa). Falls back to getClientFacingUrl()/poke when not set.
  getPokeAppUrl: (): string => {
    return EnvironmentConfig.getEnvVariable("POKE_APP_URL");
  },
  // For OAuth/WorkOS redirects. Allows overriding the redirect base URL separately
  // from NEXT_PUBLIC_DUST_API_URL. Falls back to getClientFacingUrl() when not set.
  getAuthRedirectBaseUrl: (): string => {
    return (
      EnvironmentConfig.getOptionalEnvVariable("DUST_AUTH_REDIRECT_BASE_URL") ??
      config.getApiBaseUrl()
    );
  },
  getDustInviteTokenSecret: (): string => {
    return EnvironmentConfig.getEnvVariable("DUST_INVITE_TOKEN_SECRET");
  },
  getIPInfoApiToken: (): string => {
    return EnvironmentConfig.getEnvVariable("IPINFO_API_TOKEN");
  },
  getResendApiKey: (): string => {
    return EnvironmentConfig.getEnvVariable("RESEND_API_KEY");
  },
  getInstinctWhatsAppNumber: (): string | null => {
    return (
      EnvironmentConfig.getOptionalEnvVariable("INSTINCT_WHATSAPP_NUMBER") ??
      null
    );
  },
  getResendWebhookSecret: (): string => {
    return EnvironmentConfig.getEnvVariable("RESEND_WEBHOOK_SECRET");
  },
  getAgentMailApiKey: (): string => {
    return EnvironmentConfig.getEnvVariable("AGENTMAIL_API_KEY");
  },
  getAgentMailWebhookSecret: (): string => {
    return EnvironmentConfig.getEnvVariable("AGENTMAIL_WEBHOOK_SECRET");
  },
  // Dedicated Anthropic API key scoped to our EAP workspace. Optional: only set
  // in deployments that serve EAP models (those with `useEapKey` in their config).
  getAnthropicEapApiKey: (): string | null => {
    return (
      EnvironmentConfig.getOptionalEnvVariable("ANTHROPIC_EAP_API_KEY") ?? null
    );
  },
  // Anthropic API key for Dust-managed features (e.g. the Academy quiz chat).
  // Optional: only set in deployments that serve these features.
  getDustManagedOpenAIApiKey: (): string | null => {
    return (
      EnvironmentConfig.getOptionalEnvVariable("DUST_MANAGED_OPENAI_API_KEY") ??
      null
    );
  },
  getDustManagedAnthropicApiKey: (): string | null => {
    return (
      EnvironmentConfig.getOptionalEnvVariable(
        "DUST_MANAGED_ANTHROPIC_API_KEY"
      ) ?? null
    );
  },
  // Pub/Sub topic Gmail publishes mailbox changes to
  // (`projects/<p>/topics/<t>`, with gmail-api-push@system.gserviceaccount.com
  // granted Publisher). Optional: without it the mailbox spine polls.
  getGmailPushTopic: (): string | null => {
    return EnvironmentConfig.getOptionalEnvVariable("GMAIL_PUSH_TOPIC") ?? null;
  },
  // Shared secret the Pub/Sub push subscription carries as `?token=` on the
  // endpoint URL. Required for the push route to accept anything.
  getGmailPushVerificationToken: (): string | null => {
    return (
      EnvironmentConfig.getOptionalEnvVariable(
        "GMAIL_PUSH_VERIFICATION_TOKEN"
      ) ?? null
    );
  },
  // Clay people-search API key for wiki people research. Internal only — read
  // exclusively from lib/api/wiki/research/clay.ts. Optional: research degrades to
  // signals-only when unset.
  getClayApiKey: (): string | null => {
    return EnvironmentConfig.getOptionalEnvVariable("CLAY_API_KEY") ?? null;
  },
  // ADR 0015 D2: the wiki's section writer v2 (dossiers, retrieved evidence,
  // section contracts). Off until it passes the T0 gate; the eval turns it on.
  getWikiSectionWriterV2Enabled: (): boolean => {
    return (
      EnvironmentConfig.getOptionalEnvVariable("WIKI_SECTION_WRITER_V2") ===
      "true"
    );
  },
  // Wave 17c — the dated beta header Anthropic's routine fire endpoint requires. The API is
  // experimental and ships breaking changes behind new dated header versions, so a bump is an
  // env change rather than a deploy. A workspace-level override waits until a second value
  // exists to choose between.
  getClaudeCodeRoutineBetaHeader: (): string => {
    return (
      EnvironmentConfig.getOptionalEnvVariable("CLAUDE_CODE_ROUTINE_BETA") ??
      "experimental-cc-routine-2026-04-01"
    );
  },
  getSupportEmailAddress: (): { name: string; email: string } =>
    brandSupportEmail,
  getAgentEmailDomain: (): string => brandAgentEmailDomain,
  // The founder's own inbox: the welcome mail is sent from it and replies land
  // in it, so it has to stay a real, monitored mailbox.
  getFounderEmailAddress: (): { name: string; email: string } => ({
    name: "Abhinav Japesh",
    email: brandEmailAddress("abhinav.japesh"),
  }),
  // Addresses a development stack may mail besides the company's own domain,
  // so a tester's own Gmail can receive the real mails.
  getDevEmailRecipients: (): string[] =>
    (EnvironmentConfig.getOptionalEnvVariable("EMAIL_DEV_RECIPIENTS") ?? "")
      .split(",")
      .map((address) => address.trim().toLowerCase())
      .filter((address) => address.length > 0),
  // Kill switch for the daily nudge routine: nothing is drafted or sent unless
  // it is exactly "true".
  isUserNudgesEnabled: (): boolean =>
    EnvironmentConfig.getOptionalEnvVariable("NUDGES_ENABLED") === "true",
  // The company's mailing address for the check-in email footer. US law
  // (CAN-SPAM) expects one; a PO box or registered address counts.
  getCompanyPostalAddress: (): string | undefined =>
    EnvironmentConfig.getOptionalEnvVariable("COMPANY_POSTAL_ADDRESS"),
  // The internal Slack channel where nudge drafts wait for a Send or Skip.
  getUserNudgesSlackChannelId: (): string | undefined =>
    EnvironmentConfig.getOptionalEnvVariable("NUDGES_SLACK_CHANNEL_ID"),
  getFounderCcAddresses: (): string[] => [
    brandEmailAddress("sourav.das"),
    brandEmailAddress("hardik.aggarwal"),
  ],
  getStripePublishableKey: (): string => {
    // Using process.env here to make sure the function is usable on the client side.
    if (!process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY) {
      throw new Error("NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY is not set");
    }
    return process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY;
  },
  // Phone verification needs a Turnstile captcha, a Persona number lookup and Twilio
  // SMS. The site key is the only one of the three the browser can see, so it is the
  // single switch for the whole flow on both sides: unset it and the free plan
  // activates without a phone number. Unfilled third-party credentials are carried as
  // REPLACE_ME_* placeholders in this deployment, which are truthy, so they count as
  // unset here too.
  isPhoneVerificationConfigured: (): boolean => {
    // Using process.env here to make sure the function is usable on the client side.
    const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
    return Boolean(siteKey && !siteKey.startsWith("REPLACE_ME"));
  },
  getTurnstileSiteKey: (): string => {
    // Using process.env here to make sure the function is usable on the client side.
    if (process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY) {
      return process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
    }
    // Cloudflare-published "always passes" dummy sitekey for local dev:
    // https://developers.cloudflare.com/turnstile/troubleshooting/testing/
    if (isDevelopment()) {
      // return "3x00000000000000000000FF"; // forces interactive challenge
      // return "2x00000000000000000000AB"; // always fails and visible
      return "1x00000000000000000000AA"; // always succeeds and visible
    }
    throw new Error("NEXT_PUBLIC_TURNSTILE_SITE_KEY is not set");
  },
  getTurnstileSecretKey: (): string => {
    // Cloudflare-published "always validates" dummy secret for local dev:
    // https://developers.cloudflare.com/turnstile/troubleshooting/testing/
    if (isDevelopment()) {
      // return "2x0000000000000000000000000000000AA" // always fails validation
      return "1x0000000000000000000000000000000AA"; // always passes validation
    }
    return EnvironmentConfig.getEnvVariable("TURNSTILE_SECRET_KEY");
  },
  getStripeSecretKey: (): string => {
    return EnvironmentConfig.getEnvVariable("STRIPE_SECRET_KEY");
  },
  getStripeSecretWebhookKey: (): string => {
    return EnvironmentConfig.getEnvVariable("STRIPE_SECRET_WEBHOOK_KEY");
  },
  // Twilio (workspace verification - OTP).
  getTwilioAccountSid: (): string => {
    return EnvironmentConfig.getEnvVariable("TWILIO_ACCOUNT_SID");
  },
  getTwilioAuthToken: (): string => {
    return EnvironmentConfig.getEnvVariable("TWILIO_AUTH_TOKEN");
  },
  getTwilioVerifyServiceSid: (): string => {
    return EnvironmentConfig.getEnvVariable("TWILIO_VERIFY_SERVICE_SID");
  },
  // Persona (workspace verification - phone risk).
  getPersonaApiKey: (): string => {
    return EnvironmentConfig.getEnvVariable("PERSONA_API_KEY");
  },
  getServiceAccount: (): string => {
    return EnvironmentConfig.getEnvVariable("SERVICE_ACCOUNT");
  },
  getPostHogApiKey: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable("NEXT_PUBLIC_POSTHOG_KEY");
  },
  getCustomerIoSiteId: (): string => {
    return EnvironmentConfig.getEnvVariable("CUSTOMERIO_SITE_ID");
  },
  getCustomerIoApiKey: (): string => {
    return EnvironmentConfig.getEnvVariable("CUSTOMERIO_API_KEY");
  },
  getCustomerIoEnabled: (): boolean => {
    return (
      EnvironmentConfig.getOptionalEnvVariable("CUSTOMERIO_ENABLED") === "true"
    );
  },
  isOnboardingUnlimitedPlanEnabled: (): boolean => {
    return (
      EnvironmentConfig.getOptionalEnvVariable(
        "ONBOARDING_UNLIMITED_PLAN_ENABLED"
      ) === "true"
    );
  },
  // Used for communication of front to (itself in prod) for dust-apps execution.
  getDustDevelopmentSystemAPIKey: (): string => {
    return EnvironmentConfig.getEnvVariable("DUST_DEVELOPMENT_SYSTEM_API_KEY");
  },
  getDustDevelopmentWorkspaceId: (): string => {
    return EnvironmentConfig.getEnvVariable("DUST_DEVELOPMENT_WORKSPACE_ID");
  },
  getCoreAPIConfig: (): { url: string; apiKey: string | null } => {
    return {
      url: EnvironmentConfig.getEnvVariable("CORE_API"),
      apiKey: EnvironmentConfig.getOptionalEnvVariable("CORE_API_KEY") ?? null,
    };
  },
  getConnectorsAPIConfig: (): {
    url: string;
    secret: string;
    webhookSecret: string;
  } => {
    return {
      url: EnvironmentConfig.getEnvVariable("CONNECTORS_API"),
      secret: EnvironmentConfig.getEnvVariable("DUST_CONNECTORS_SECRET"),
      webhookSecret: EnvironmentConfig.getEnvVariable(
        "DUST_CONNECTORS_WEBHOOKS_SECRET"
      ),
    };
  },
  getDustAPIConfig: (): {
    url: string;
    nodeEnv: string;
    isProdApiUrlConfigured: boolean;
  } => {
    const prodApiUrl =
      EnvironmentConfig.getOptionalEnvVariable("DUST_PROD_API");
    return {
      // Memoair production API URL is hardcoded for now.
      url: prodApiUrl ?? PRODUCTION_DUST_API,
      nodeEnv:
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        EnvironmentConfig.getOptionalEnvVariable("NODE_ENV") || "development",
      isProdApiUrlConfigured: Boolean(prodApiUrl),
    };
  },
  getVizJwtSecret: (): string => {
    return EnvironmentConfig.getEnvVariable("VIZ_JWT_SECRET");
  },
  getLiveJwtSecret: (): string => {
    return EnvironmentConfig.getEnvVariable("LIVE_JWT_SECRET");
  },
  // Browser WebSocket URL of live-server: wss://<host>/live/ws behind Caddy, ws://localhost:3020
  // in dev. No trailing slash: the provider strips it and Caddy's /live/* matcher then misses.
  getLivePublicUrl: (): string => {
    return EnvironmentConfig.getEnvVariable("LIVE_PUBLIC_URL");
  },
  // Server-to-server base URL for agent ops, e.g. http://live-server:3020 on the VM.
  getLiveInternalUrl: (): string => {
    return EnvironmentConfig.getEnvVariable("LIVE_INTERNAL_URL");
  },
  getAcademyJwtSecret: (): string => {
    return EnvironmentConfig.getEnvVariable("DUST_ACADEMY_JWT_SECRET");
  },
  getSandboxJwtSecret: (): string => {
    return EnvironmentConfig.getEnvVariable("DUST_SANDBOX_JWT_SECRET");
  },
  getEgressProxyJwtSecret: (): string => {
    return EnvironmentConfig.getEnvVariable("EGRESS_PROXY_JWT_SECRET");
  },
  getEgressProxyHost: (): string => {
    return EnvironmentConfig.getEnvVariable("EGRESS_PROXY_HOST");
  },
  getEgressProxyPort: (): number => {
    const value =
      EnvironmentConfig.getOptionalEnvVariable("EGRESS_PROXY_PORT") ?? "4443";
    const port = Number.parseInt(value, 10);

    if (Number.isNaN(port) || port <= 0) {
      throw new Error("EGRESS_PROXY_PORT must be a positive integer");
    }

    return port;
  },
  getEgressProxyTlsName: (): string => {
    return (
      EnvironmentConfig.getOptionalEnvVariable("EGRESS_PROXY_TLS_NAME") ??
      config.getEgressProxyHost()
    );
  },
  getEgressPolicyBucket: (): string => {
    return EnvironmentConfig.getEnvVariable("EGRESS_PROXY_POLICY_BUCKET");
  },
  getEgressProxyInternalUrl: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable(
      "EGRESS_PROXY_INTERNAL_URL"
    );
  },
  getOAuthAPIConfig: (): { url: string; apiKey: string | null } => {
    return {
      url: EnvironmentConfig.getEnvVariable("OAUTH_API"),
      apiKey: EnvironmentConfig.getOptionalEnvVariable("OAUTH_API_KEY") ?? null,
    };
  },
  getRegionResolverSecret: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable("REGION_RESOLVER_SECRET");
  },
  getRegion: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable("REGION");
  },
  // OAuth
  getOAuthGithubApp: (): string => {
    return EnvironmentConfig.getEnvVariable("OAUTH_GITHUB_APP");
  },
  getOAuthGithubAppPlatformActions: (): string => {
    return EnvironmentConfig.getEnvVariable(
      "OAUTH_GITHUB_APP_PLATFORM_ACTIONS"
    );
  },
  getOAuthGithubAppPersonalActions: (): string => {
    return EnvironmentConfig.getEnvVariable(
      "OAUTH_GITHUB_APP_PLATFORM_ACTIONS_CLIENT_ID"
    );
  },
  getOAuthGithubAppWebhooks: (): string => {
    return EnvironmentConfig.getEnvVariable(
      "OAUTH_GITHUB_APP_WEBHOOKS_CLIENT_ID"
    );
  },
  getOAuthNotionClientId: (): string => {
    return EnvironmentConfig.getEnvVariable("OAUTH_NOTION_CLIENT_ID");
  },
  getOAuthNotionPlatformActionsClientId: (): string => {
    return EnvironmentConfig.getEnvVariable(
      "OAUTH_NOTION_PLATFORM_ACTIONS_CLIENT_ID"
    );
  },
  getOAuthConfluenceClientId: (): string => {
    return EnvironmentConfig.getEnvVariable("OAUTH_CONFLUENCE_CLIENT_ID");
  },
  getOAuthConfluenceToolsClientId: (): string => {
    return EnvironmentConfig.getEnvVariable("OAUTH_CONFLUENCE_TOOLS_CLIENT_ID");
  },
  getOAuthGoogleDriveClientId: (): string => {
    return EnvironmentConfig.getEnvVariable("OAUTH_GOOGLE_DRIVE_CLIENT_ID");
  },
  getGoogleDrivePickerApiKey: (): string => {
    return EnvironmentConfig.getEnvVariable("GOOGLE_DRIVE_PICKER_API_KEY");
  },
  getMemoairSupportSlack: (): {
    botToken: string;
    signingSecret: string;
    channelId: string;
  } | null => {
    const botToken = EnvironmentConfig.getOptionalEnvVariable(
      "MEMOAIR_SUPPORT_SLACK_BOT_TOKEN"
    );
    const signingSecret = EnvironmentConfig.getOptionalEnvVariable(
      "MEMOAIR_SUPPORT_SLACK_SIGNING_SECRET"
    );
    const channelId = EnvironmentConfig.getOptionalEnvVariable(
      "MEMOAIR_SUPPORT_SLACK_CHANNEL_ID"
    );
    return botToken && signingSecret && channelId
      ? { botToken, signingSecret, channelId }
      : null;
  },
  getMemoairGuideEscalationSlackChannelId: (): string | null => {
    return (
      EnvironmentConfig.getOptionalEnvVariable(
        "MEMOAIR_GUIDE_ESCALATION_SLACK_CHANNEL_ID"
      ) || null
    );
  },
  getOAuthSlackClientId: (): string => {
    return EnvironmentConfig.getEnvVariable("OAUTH_SLACK_CLIENT_ID");
  },
  getOAuthSlackBotClientId: (): string => {
    return EnvironmentConfig.getEnvVariable("OAUTH_SLACK_BOT_CLIENT_ID");
  },
  getOAuthSlackToolsClientId: (): string => {
    return EnvironmentConfig.getEnvVariable("OAUTH_SLACK_TOOLS_CLIENT_ID");
  },
  getOAuthIntercomClientId: (): string => {
    return EnvironmentConfig.getEnvVariable("OAUTH_INTERCOM_CLIENT_ID");
  },
  getOAuthGongClientId: (): string => {
    return EnvironmentConfig.getEnvVariable("OAUTH_GONG_CLIENT_ID");
  },
  getOAuthMicrosoftClientId: (): string => {
    return EnvironmentConfig.getEnvVariable("OAUTH_MICROSOFT_CLIENT_ID");
  },
  getOAuthMicrosoftToolsClientId: (): string => {
    return EnvironmentConfig.getEnvVariable("OAUTH_MICROSOFT_TOOLS_CLIENT_ID");
  },
  getOAuthZendeskClientId: (): string => {
    return EnvironmentConfig.getEnvVariable("OAUTH_ZENDESK_CLIENT_ID");
  },
  getOAuthProductboardClientId: (): string => {
    return EnvironmentConfig.getEnvVariable("OAUTH_PRODUCTBOARD_CLIENT_ID");
  },
  getOAuthHubspotClientId: (): string => {
    return EnvironmentConfig.getEnvVariable("OAUTH_HUBSPOT_CLIENT_ID");
  },
  getOAuthFreshserviceClientId: (): string => {
    return EnvironmentConfig.getEnvVariable("OAUTH_FRESHWORKS_CLIENT_ID");
  },
  getOAuthFreshserviceDomain: (): string => {
    return EnvironmentConfig.getEnvVariable("OAUTH_FRESHWORKS_DOMAIN");
  },
  getOAuthJiraClientId: (): string => {
    return EnvironmentConfig.getEnvVariable("OAUTH_JIRA_CLIENT_ID");
  },
  getOAuthMondayClientId: (): string => {
    return EnvironmentConfig.getEnvVariable("OAUTH_MONDAY_CLIENT_ID");
  },
  getOAuthDiscordClientId: (): string => {
    return EnvironmentConfig.getEnvVariable("OAUTH_DISCORD_CLIENT_ID");
  },
  getOAuthFathomClientId: (): string => {
    return EnvironmentConfig.getEnvVariable("OAUTH_FATHOM_CLIENT_ID");
  },
  getDevOAuthRedirectBaseUrl: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable(
      "DEV_OAUTH_REDIRECT_BASE_URL"
    );
  },
  getOAuthLinearClientId: (): string => {
    return EnvironmentConfig.getEnvVariable("OAUTH_LINEAR_CLIENT_ID");
  },

  // Text extraction.
  getTextExtractionUrl: (): string => {
    return EnvironmentConfig.getEnvVariable("TEXT_EXTRACTION_URL");
  },
  getDocumentRendererUrl: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable("DOCUMENT_RENDERER_URL");
  },
  // In local dev Gotenberg runs in Docker and cannot reach localhost.
  // Set DOCUMENT_RENDERER_APP_URL=http://host.docker.internal:3011 in .env.local.
  getDocumentRendererAppUrl: (): string => {
    return (
      EnvironmentConfig.getOptionalEnvVariable("DOCUMENT_RENDERER_APP_URL") ??
      config.getAppUrl()
    );
  },
  // API URL reachable from Gotenberg's Chromium (inside Docker).
  // Set DOCUMENT_RENDERER_API_URL=http://host.docker.internal:3000 in .env.local.
  getDocumentRendererApiUrl: (): string => {
    return (
      EnvironmentConfig.getOptionalEnvVariable("DOCUMENT_RENDERER_API_URL") ??
      config.getApiBaseUrl()
    );
  },
  // Public viz URL (used by Gotenberg which routes through egress proxy).
  getVizPublicUrl: (): string => {
    return EnvironmentConfig.getEnvVariable("VIZ_PUBLIC_URL");
  },
  // Status page.
  getStatusPageProvidersPageId: (): string => {
    return EnvironmentConfig.getEnvVariable("STATUS_PAGE_PROVIDERS_PAGE_ID");
  },
  getStatusPageDustPageId: (): string => {
    return EnvironmentConfig.getEnvVariable("STATUS_PAGE_DUST_PAGE_ID");
  },
  getStatusPageApiToken: (): string => {
    return EnvironmentConfig.getEnvVariable("STATUS_PAGE_API_TOKEN");
  },
  getMultiActionsAgentAnthropicBetaFlags: (): string[] | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable(
      "MULTI_ACTIONS_AGENT_ANTHROPIC_BETA_FLAGS"
    )?.split(",");
  },

  // WorkOS
  getWorkOSApiKey: (): string => {
    return EnvironmentConfig.getEnvVariable("WORKOS_API_KEY");
  },
  getWorkOSClientId: (): string => {
    return EnvironmentConfig.getEnvVariable("WORKOS_CLIENT_ID");
  },
  getWorkOSCookiePassword: (): string => {
    return EnvironmentConfig.getEnvVariable("WORKOS_COOKIE_PASSWORD");
  },
  getWorkOSIssuerURL: (): string => {
    return EnvironmentConfig.getEnvVariable("WORKOS_ISSUER_URL");
  },
  getWorkOSWebhookSecret: (): string => {
    return EnvironmentConfig.getEnvVariable("WORKOS_WEBHOOK_SECRET");
  },
  getWorkOSWebhookSigningSecret: (): string => {
    return EnvironmentConfig.getEnvVariable("WORKOS_WEBHOOK_SIGNING_SECRET");
  },
  getWorkOSActionSecret: (): string => {
    return EnvironmentConfig.getEnvVariable("WORKOS_ACTION_SECRET");
  },
  getWorkOSActionSigningSecret: (): string => {
    return EnvironmentConfig.getEnvVariable("WORKOS_ACTION_SIGNING_SECRET");
  },
  getWorkOSSessionCookieDomain: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable(
      "WORKOS_SESSION_COOKIE_DOMAIN"
    );
  },
  getWorkOSSessionCookieSecureFlag: (): string => {
    return config.getWorkOSSessionCookieDomain() ? "; Secure" : "";
  },
  getWorkOSEnvironmentId: (): string => {
    return EnvironmentConfig.getEnvVariable("WORKOS_ENVIRONMENT_ID");
  },
  getWorkOSAuthKitDomain: (): string => {
    return EnvironmentConfig.getEnvVariable("WORKOS_AUTHKIT_DOMAIN");
  },
  getWorkOSApiHostname: (): string => {
    return (
      EnvironmentConfig.getOptionalEnvVariable("WORKOS_API_HOSTNAME") ??
      "auth-api.dust.tt"
    );
  },
  // Profiler.
  getProfilerSecret: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable("DEBUG_PROFILER_SECRET");
  },
  getComposioApiKey: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable("COMPOSIO_API_KEY");
  },
  getApolloApiKey: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable("APOLLO_API_KEY");
  },
  getJevApiKey: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable("DUST_MANAGED_JEV_API_KEY");
  },
  // The live-voice service that hears and speaks in calls, e.g. http://live-voice:3040. Unset,
  // calls keep browser transcription and answers in chat.
  getLiveVoiceInternalUrl: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable("LIVE_VOICE_INTERNAL_URL");
  },
  // The LiveKit server live-voice joins calls on, and the pair that signs its participants. Same
  // values as live-server's.
  getLiveKitUrl: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable("LIVEKIT_URL");
  },
  getLiveKitApiKey: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable("LIVEKIT_API_KEY");
  },
  getLiveKitApiSecret: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable("LIVEKIT_API_SECRET");
  },
  getCartesiaApiKey: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable(
      "DUST_MANAGED_CARTESIA_API_KEY"
    );
  },
  getDeepgramApiKey: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable(
      "DUST_MANAGED_DEEPGRAM_API_KEY"
    );
  },
  // Huddle prices in USD, the owner's numbers (provider cost plus markup). Unset, that part of a
  // huddle is recorded but charged at zero.
  getHuddleTranscriptionUsdPerHour: (): number =>
    Number(
      EnvironmentConfig.getOptionalEnvVariable(
        "MEMOAIR_HUDDLE_TRANSCRIPTION_USD_PER_HOUR"
      ) ?? 0
    ),
  getHuddleSpeechUsdPerThousandCharacters: (): number =>
    Number(
      EnvironmentConfig.getOptionalEnvVariable(
        "MEMOAIR_HUDDLE_SPEECH_USD_PER_1K_CHARS"
      ) ?? 0
    ),
  getHuddleDecisionUsd: (): number =>
    Number(
      EnvironmentConfig.getOptionalEnvVariable("MEMOAIR_HUDDLE_DECISION_USD") ??
        0
    ),
  // "<provider>/<model id>" the call's fast talker answers on; the agent's own loop does the work.
  getVoiceTalkerModel: (): string => {
    return (
      EnvironmentConfig.getOptionalEnvVariable("MEMOAIR_VOICE_TALKER_MODEL") ??
      "anthropic/claude-haiku-5-5"
    );
  },
  getRedisUri: (): string => {
    return EnvironmentConfig.getEnvVariable("REDIS_URI");
  },
  getRedisCacheUri: (): string => {
    return EnvironmentConfig.getEnvVariable("REDIS_CACHE_URI");
  },
  getContentfulSpaceId: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable("CONTENTFUL_SPACE_ID");
  },
  getContentfulAccessToken: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable("CONTENTFUL_ACCESS_TOKEN");
  },
  getContentfulEnvironment: (): string => {
    return (
      EnvironmentConfig.getOptionalEnvVariable("CONTENTFUL_ENVIRONMENT") ??
      "master"
    );
  },
  getContentfulPreviewSecret: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable(
      "CONTENTFUL_PREVIEW_SECRET"
    );
  },
  getContentfulPreviewToken: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable("CONTENTFUL_PREVIEW_TOKEN");
  },
  // Untrusted egress proxy.
  getUntrustedEgressProxyHost: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable(
      "UNTRUSTED_EGRESS_PROXY_HOST"
    );
  },
  getUntrustedEgressProxyPort: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable(
      "UNTRUSTED_EGRESS_PROXY_PORT"
    );
  },
  getElasticsearchConfig: (): {
    url: string;
    username: string;
    password: string;
  } => {
    return {
      url: EnvironmentConfig.getEnvVariable("ELASTICSEARCH_URL"),
      username: EnvironmentConfig.getEnvVariable("ELASTICSEARCH_USERNAME"),
      password: EnvironmentConfig.getEnvVariable("ELASTICSEARCH_PASSWORD"),
    };
  },
  isLangfuseEnabled: (): boolean => {
    const isEnabled =
      EnvironmentConfig.getOptionalEnvVariable(
        "LANGFUSE_ENABLED"
      )?.toLowerCase() === "true";

    if (isEnabled) {
      // If enabled, ensure that all keys are present.
      EnvironmentConfig.getEnvVariable("LANGFUSE_PUBLIC_KEY");
      EnvironmentConfig.getEnvVariable("LANGFUSE_SECRET_KEY");
      EnvironmentConfig.getOptionalEnvVariable("LANGFUSE_BASE_URL");
    }

    return isEnabled;
  },
  getLangfuseClientConfig: (): {
    publicKey: string;
    secretKey: string;
    baseUrl: string | undefined;
  } => {
    return {
      publicKey: EnvironmentConfig.getEnvVariable("LANGFUSE_PUBLIC_KEY"),
      secretKey: EnvironmentConfig.getEnvVariable("LANGFUSE_SECRET_KEY"),
      baseUrl: EnvironmentConfig.getOptionalEnvVariable("LANGFUSE_BASE_URL"),
    };
  },
  getLangfuseUiBaseUrl: () => {
    return EnvironmentConfig.getOptionalEnvVariable("LANGFUSE_UI_BASE_URL");
  },
  getTemporalConnectorsNamespace: () => {
    return EnvironmentConfig.getOptionalEnvVariable(
      "TEMPORAL_CONNECTORS_NAMESPACE"
    );
  },
  getTemporalAgentNamespace: () => {
    return EnvironmentConfig.getOptionalEnvVariable("TEMPORAL_AGENT_NAMESPACE");
  },
  getTemporalFrontNamespace: () => {
    return EnvironmentConfig.getOptionalEnvVariable("TEMPORAL_NAMESPACE");
  },
  // Email.
  getEmailWebhookSecret: (): string => {
    return EnvironmentConfig.getEnvVariable("EMAIL_WEBHOOK_SECRET");
  },
  getProductionDustWorkspaceId: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable(
      "PRODUCTION_DUST_WORKSPACE_ID"
    );
  },
  // Email validation secret for HMAC signing of action approval tokens.
  getEmailValidationSecret: (): string => {
    return EnvironmentConfig.getEnvVariable("EMAIL_VALIDATION_SECRET");
  },
  // Secret for signing gated asset download tokens (ebooks, whitepapers, etc.).
  getGatedAssetsTokenSecret: (): string => {
    return EnvironmentConfig.getEnvVariable("GATED_ASSETS_TOKEN_SECRET");
  },
  // Secrets for secure storage of keys and bearer tokens.
  getDeveloperSecretsSecret: (): string => {
    return EnvironmentConfig.getEnvVariable("DUST_DEVELOPERS_SECRETS_SECRET");
  },
  getMCPServerCredentialsSecret: (): string => {
    return EnvironmentConfig.getEnvVariable(
      "DUST_MCP_SERVER_CREDENTIALS_SECRET"
    );
  },
  // E2B Sandbox.
  getE2BSandboxConfig: (): {
    apiKey: string;
    domain: string | undefined;
    sandboxLifetimeHours: number | undefined;
  } => {
    const lifetimeHours = EnvironmentConfig.getOptionalEnvVariable(
      "E2B_SANDBOX_LIFETIME_HOURS"
    );
    return {
      apiKey: EnvironmentConfig.getEnvVariable("E2B_API_KEY"),
      domain: EnvironmentConfig.getOptionalEnvVariable("E2B_DOMAIN"),
      sandboxLifetimeHours:
        lifetimeHours && Number(lifetimeHours) > 0
          ? Number(lifetimeHours)
          : undefined,
    };
  },
  getDatadogApiKey: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable("DD_API_KEY");
  },
  getSandboxDevFrontHostName: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable(
      "SBX_DEV_FRONT_URL"
    )?.replace(/^https?:\/\//, "");
  },
  // Dev-only switch to fully unrestrict sandbox network egress: skips the
  // dsbx forwarder, tears down in-sandbox nftables redirect, and lets E2B
  // allow all outbound traffic. Only honored when isDevelopment() to avoid
  // accidental enablement in production.
  getSandboxDevUnrestrictedEgress: (): boolean => {
    if (!isDevelopment()) {
      return false;
    }
    return (
      EnvironmentConfig.getOptionalEnvVariable(
        "SBX_DEV_UNRESTRICTED_EGRESS"
      ) === "true"
    );
  },
  getSandboxGcpArtifactServiceAccountPath: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable(
      "SBX_GCP_ARTIFACT_SERVICE_ACCOUNT"
    );
  },
  getSandboxGcpArtifactRegistry: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable(
      "SBX_GCP_ARTIFACT_REGISTRY"
    );
  },
  getGoogleCloudProjectId: (): string => {
    return EnvironmentConfig.getEnvVariable("GOOGLE_CLOUD_PROJECT_ID");
  },
  // Novu notifications.
  getNovuSecretKey: (): string => {
    return EnvironmentConfig.getEnvVariable("NOVU_SECRET_KEY");
  },
  getNovuApiUrl: (): string => {
    // Using process.env here to make sure the function is usable on the client side.
    if (!process.env.NEXT_PUBLIC_NOVU_API_URL) {
      throw new Error("NEXT_PUBLIC_NOVU_API_URL is not set");
    }
    return process.env.NEXT_PUBLIC_NOVU_API_URL;
  },
  // Metronome billing.
  getMetronomeApiKey: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable("METRONOME_API_KEY");
  },
  getMetronomeWebhookSecret: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable("METRONOME_WEBHOOK_SECRET");
  },
  // Pins which Stripe billing-provider delivery method (connection) to use when
  // the Metronome org has more than one Stripe DIRECT_TO_BILLING_PROVIDER
  // configuration (the bare delivery_method is ambiguous and rejected). Must be
  // a delivery_method_id (UUID), discoverable via
  // `scripts/list_metronome_delivery_methods.ts`. Leave unset when the org has a
  // single Stripe connection (e.g. prod); Metronome then resolves the bare
  // delivery_method.
  getMetronomeStripeDeliveryMethodId: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable(
      "METRONOME_STRIPE_DELIVERY_METHOD_ID"
    );
  },
  getDevEnvName: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable("DEV_ENV_NAME");
  },
  getVertexAiProjectId: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable("VERTEX_AI_PROJECT_ID");
  },
  getDustWebhooksPublicUrl: (): string | undefined => {
    return EnvironmentConfig.getOptionalEnvVariable("DUST_WEBHOOKS_PUBLIC_URL");
  },
  getConvertAPIKey: (): string => {
    return EnvironmentConfig.getEnvVariable("CONVERTAPI_API_KEY");
  },
  getImgproxyUrl: (): string => {
    return EnvironmentConfig.getEnvVariable("IMGPROXY_URL");
  },
  getImgproxyKey: (): string => {
    return EnvironmentConfig.getEnvVariable("IMGPROXY_KEY");
  },
  getImgproxySalt: (): string => {
    return EnvironmentConfig.getEnvVariable("IMGPROXY_SALT");
  },
};

export default config;
